import { RARITY } from "../config/constants.js";
import { pick, rndi, weightedPick } from "../core/util.js";
import { ensureMemory } from "../systems/RelicMemory.js";
import { relicName } from "../config/helix.js";

/**
 * Elemental enchantments. Weapons can roll one; the effect POWER scales with
 * rarity (mult) and floor depth. Staves/wands are innately magical and deal
 * AoE on impact (see `aoe`). CombatSystem reads `element`/`elementPower` to
 * apply burn/slow/poison/stun status to enemies, and Effects paints the VFX.
 */
export const ELEMENTS = {
  fire:   { name: "Flaming",  color: "#ff6a3d", verb: "burn" },
  frost:  { name: "Frost",    color: "#5fd0ff", verb: "slow" },
  poison: { name: "Venomous", color: "#7bd645", verb: "poison" },
  shock:  { name: "Shocking", color: "#ffe14d", verb: "stun" },
  arcane: { name: "Arcane",   color: "#c77dff", verb: "blast" },
};
export const ELEMENT_COLORS = Object.fromEntries(Object.entries(ELEMENTS).map(([k, v]) => [k, v.color]));
export const ELEMENT_KEYS = ["fire", "frost", "poison", "shock"];
const ENCHANT_CHANCE = [0.06, 0.3, 0.62, 1.0];   // by rarity index (Common→Epic)

// Base weapon + armor templates (all KayKit Adventurers gear). Stats get
// scaled by rarity and floor depth; every entry can drop as loot.
export const WEAPONS = [
  { name: "Short Sword",    wtype: "melee",  dmg: [6, 10],  range: 2.6, cd: 0.42, color: "#b9b9b9", model: "weapon/sword" },
  { name: "Greatsword",     wtype: "melee",  dmg: [14, 22], range: 3.0, cd: 0.55, color: "#dfe4ea", model: "weapon/sword2" },
  { name: "Hand Axe",       wtype: "melee",  dmg: [8, 13],  range: 2.5, cd: 0.40, color: "#cbb28a", model: "weapon/axe" },
  { name: "Battle Axe",     wtype: "melee",  dmg: [17, 26], range: 2.8, cd: 0.64, color: "#c9a24b", model: "weapon/axe2" },
  { name: "Dagger",         wtype: "melee",  dmg: [5, 8],   range: 2.2, cd: 0.20, color: "#a29bfe", model: "weapon/dagger" },
  { name: "Longbow",        wtype: "ranged", dmg: [9, 14],  range: 16,  cd: 0.5,  color: "#8bc34a", model: "weapon/bow" },
  { name: "Heavy Crossbow", wtype: "ranged", dmg: [14, 20], range: 15,  cd: 0.78, color: "#b08050", model: "weapon/crossbow" },
  { name: "Frost Staff",    wtype: "ranged", dmg: [12, 18], range: 15,  cd: 0.62, color: "#4fc3f7", model: "weapon/staff", magic: true, aoe: 2.8, innate: "frost" },
  { name: "Ember Wand",     wtype: "ranged", dmg: [9, 14],  range: 14,  cd: 0.5,  color: "#ff7a3d", model: "weapon/wand",  magic: true, aoe: 2.2, innate: "fire" },
];

export const ARMORS = [
  { name: "Cloth Tunic",   def: 2,  hp: 10, color: "#8d6e63" },
  { name: "Leather Vest",  def: 4,  hp: 20, color: "#a1673a" },
  { name: "Chain Mail",    def: 7,  hp: 35, color: "#b0bec5" },
  { name: "Plate Armor",   def: 11, hp: 55, color: "#cfd8dc" },
  { name: "Round Shield",  def: 6,  hp: 8,  color: "#b5423a", model: "weapon/shield" },
  { name: "Kite Shield",   def: 8,  hp: 12, color: "#8fb8ff", model: "weapon/shield_square" },
  { name: "Spiked Shield", def: 9,  hp: 8,  color: "#c9a24b", model: "weapon/shield_spikes" },
];

function rollRarity() { return weightedPick(RARITY); }

// Class-specific starting kits (always plain Common so upgrades matter).
// `mana` = the class's base mana pool (melee classes get little; casters lots).
const STARTERS = {
  knight:    { mana: 20,  w: { name: "Knight's Longsword", wtype: "melee",  dmg: [7, 11],  range: 2.7, cd: 0.42, color: "#dfe4ea", model: "weapon/sword" },  a: { name: "Steel Plate",     def: 6, hp: 20, color: "#cfd8dc", model: "weapon/shield" } },
  barbarian: { mana: 15,  w: { name: "Savage Greataxe",    wtype: "melee",  dmg: [13, 19], range: 2.8, cd: 0.60, color: "#c9a24b", model: "weapon/axe2" },   a: { name: "Hide Harness",    def: 3, hp: 30, color: "#8a5a3a" } },
  rogue:     { mana: 35,  w: { name: "Twin Daggers",       wtype: "melee",  dmg: [6, 9],   range: 2.2, cd: 0.20, color: "#a29bfe", model: "weapon/dagger" },  a: { name: "Leather Garb",    def: 4, hp: 14, color: "#5a7d4a" } },
  ranger:    { mana: 55,  w: { name: "Hunter's Longbow",   wtype: "ranged", dmg: [9, 14],  range: 16,  cd: 0.50, color: "#8bc34a", model: "weapon/bow" },     a: { name: "Ranger's Leathers", def: 4, hp: 16, color: "#4a6b3a" } },
  mage:      { mana: 120, w: { name: "Apprentice Staff",   wtype: "ranged", dmg: [9, 14],  range: 14,  cd: 0.60, color: "#8b7bd6", model: "weapon/staff", magic: true, aoe: 2.4, element: "arcane" }, a: { name: "Mage Robes", def: 2, hp: 10, color: "#6a4a8a" } },
};

/** Build the starting weapon + armor + mana pool for a chosen class. */
export function starterKit(classId) {
  const s = STARTERS[classId] || STARTERS.knight;
  const weapon = {
    kind: "weapon", name: s.w.name, wtype: s.w.wtype, range: s.w.range, cd: s.w.cd, color: s.w.color,
    rarity: RARITY[0], model: s.w.model, dmgMin: s.w.dmg[0], dmgMax: s.w.dmg[1],
    element: s.w.element || null, elementPower: s.w.element ? 0.9 : 0, magic: !!s.w.magic, aoe: s.w.aoe || 0,
  };
  const armor = { kind: "armor", name: s.a.name, color: s.a.color, rarity: RARITY[0], def: s.a.def, hp: s.a.hp, model: s.a.model || null };
  ensureMemory(weapon, classId); ensureMemory(armor, classId);
  return { weapon, armor, maxMana: s.mana };
}

// ---- Food (real Kenney food models). Eating a food HEALS instantly AND fills
// one of 3 stacking buff slots. Each model is tinted a solid colour at runtime
// (see FOOD_TINTS), so it reads as recognizable low-poly food. ----
export const FOODS = [
  { id: "apple",     name: "Crisp Apple",        model: "food/apple",     color: "#d0392f", heal: 15, buffs: { stamRegen: 6 } },
  { id: "bread",     name: "Crusty Bread",       model: "food/bread",     color: "#c9a24a", heal: 25, buffs: { maxStam: 15 } },
  { id: "cheese",    name: "Wedge of Cheese",    model: "food/cheese",    color: "#e8c14a", heal: 12, buffs: { maxStam: 25 } },
  { id: "banana",    name: "Ripe Banana",        model: "food/banana",    color: "#e8d24a", heal: 10, buffs: { stamRegen: 8 } },
  { id: "carrot",    name: "Fresh Carrot",       model: "food/carrot",    color: "#e0802f", heal: 8,  buffs: { stamRegen: 5, maxStam: 10 } },
  { id: "cookie",    name: "Sweet Cookie",       model: "food/cookie",    color: "#b57a3a", heal: 10, buffs: { manaRegen: 5 } },
  { id: "croissant", name: "Buttery Croissant",  model: "food/croissant", color: "#d9a64a", heal: 18, buffs: { maxStam: 20 } },
  { id: "corn",      name: "Roasted Corn",       model: "food/corn",      color: "#e6c84a", heal: 14, buffs: { stamRegen: 6 } },
  { id: "cupcake",   name: "Frosted Cupcake",    model: "food/cupcake",   color: "#e88ab0", heal: 12, buffs: { maxMana: 25, manaRegen: 4 } },
  { id: "burger",    name: "Hearty Burger",      model: "food/burger",    color: "#8a5a3a", heal: 35, buffs: { maxStam: 35 } },
  { id: "soup",      name: "Bowl of Broth",      model: "food/soup",      color: "#c98a4a", heal: 30, buffs: { stamRegen: 6, maxMana: 15 } },
  { id: "cake",      name: "Slice of Cake",      model: "food/cake",      color: "#e0a0c0", heal: 20, buffs: { maxStam: 20, maxMana: 20 } },
  { id: "coffee",    name: "Hot Coffee",         model: "food/coffee",    color: "#6a4a3a", heal: 5,  buffs: { stamRegen: 10, manaRegen: 4 } },
  { id: "bacon",     name: "Strip of Bacon",     model: "food/bacon",     color: "#b5423a", heal: 16, buffs: { maxStam: 15, stamRegen: 4 } },
];

// model key → solid tint (the converted food glbs ship with a broken 1×1 texture)
export const FOOD_TINTS = Object.fromEntries(FOODS.map((f) => [f.model, f.color]));

export function makeFood() {
  const f = pick(FOODS);
  return { kind: "food", id: f.id, name: f.name, model: f.model, color: f.color, heal: f.heal, buffs: f.buffs, rarity: RARITY[1] };
}
export function foodDesc(item) {
  const b = item.buffs || {};
  const parts = [];
  if (item.heal) parts.push(`heals ${item.heal} HP`);
  for (const [k, label] of [["stamRegen", "stamina regen"], ["maxStam", "max stamina"], ["manaRegen", "mana regen"], ["maxMana", "max mana"]])
    if (b[k]) parts.push(`+${b[k]} ${label}`);
  return parts.join(", ");
}

/**
 * Build a weapon from an EXPLICIT base + rarity. `elementSpec`:
 *   undefined → roll an enchant by rarity (normal loot behaviour)
 *   null      → no enchant (unless the base is innately magical)
 *   "<key>"   → force that element
 */
function buildWeapon(base, rar, floor = 1, elementSpec) {
  const lvl = 1 + floor * 0.08;
  const ri = RARITY.indexOf(rar);
  let element = base.innate || null;
  if (elementSpec === undefined) {
    if (!element && Math.random() < (ENCHANT_CHANCE[ri] || 0)) element = pick(ELEMENT_KEYS);
  } else if (elementSpec) {
    element = elementSpec;                 // forced element
  }
  const elementPower = element ? rar.mult * (1 + floor * 0.06) : 0;
  let name = base.name;
  if (element && !base.innate) name = ELEMENTS[element].name + " " + base.name;
  const w = {
    kind: "weapon", name, wtype: base.wtype,
    range: base.range, cd: base.cd, color: element ? ELEMENTS[element].color : base.color,
    rarity: rar, model: base.model,
    dmgMin: Math.round(base.dmg[0] * rar.mult * lvl),
    dmgMax: Math.round(base.dmg[1] * rar.mult * lvl),
    element, elementPower,
    magic: !!base.magic, aoe: base.aoe || 0,
  };
  ensureMemory(w);
  return w;
}

function buildArmor(base, rar, floor = 1) {
  const lvl = 1 + floor * 0.06;
  const a = {
    kind: "armor", name: base.name, color: base.color, rarity: rar, model: base.model || null,
    def: Math.round(base.def * rar.mult * lvl),
    hp: Math.round(base.hp * rar.mult * lvl),
  };
  ensureMemory(a);
  return a;
}

const clampRar = (i) => RARITY[Math.max(0, Math.min(i | 0, RARITY.length - 1))];

export function makeWeapon(floor = 1, minRarityIdx = 0) {
  let rar = rollRarity();
  if (RARITY.indexOf(rar) < minRarityIdx) rar = clampRar(minRarityIdx);
  return buildWeapon(pick(WEAPONS), rar, floor);
}

export function makeArmor(floor = 1, minRarityIdx = 0) {
  let rar = rollRarity();
  if (RARITY.indexOf(rar) < minRarityIdx) rar = clampRar(minRarityIdx);
  return buildArmor(pick(ARMORS), rar, floor);
}

// ---- Explicit spawners for the God Menu (pick exactly what you want) ----
export function spawnWeapon(baseName, rarityIdx = 2, element = undefined, floor = 1) {
  const base = WEAPONS.find((w) => w.name === baseName) || WEAPONS[0];
  return buildWeapon(base, clampRar(rarityIdx), floor, element === "none" ? null : element);
}
export function spawnArmor(baseName, rarityIdx = 2, floor = 1) {
  const base = ARMORS.find((a) => a.name === baseName) || ARMORS[0];
  return buildArmor(base, clampRar(rarityIdx), floor);
}
export function spawnFood(id) {
  const f = FOODS.find((x) => x.id === id) || FOODS[0];
  return { kind: "food", id: f.id, name: f.name, model: f.model, color: f.color, heal: f.heal, buffs: f.buffs, rarity: RARITY[1] };
}
export function spawnResource(name) {
  const b = RESOURCES.find((r) => r.name === name) || RESOURCES[0];
  return { kind: "resource", name: b.name, model: b.model, rarity: RARITY[0], color: b.color, qty: 3 };
}
export function spawnTreasure(name, floor = 1) {
  const t = TREASURES.find((x) => x.name === name) || TREASURES[0];
  const rar = RARITY[2];
  const value = Math.max(6, Math.round(t.base * rar.mult * (1 + floor * 0.18)));
  return { kind: "treasure", name: t.name, model: t.model, color: t.color, rarity: rar, value, flavor: "A relic of the Vault. Sell it at the Shop for gold." };
}
// potion variants: "hp" | "hp_greater" | "mana" | "mana_greater"
export function spawnPotion(kind = "hp", floor = 1) {
  if (kind === "mana" || kind === "mana_greater") {
    const greater = kind === "mana_greater";
    return { kind: "potion", potType: "mana", name: greater ? "Greater Mana Potion" : "Mana Potion",
      mana: greater ? 90 : 45, rarity: greater ? RARITY[2] : RARITY[1],
      model: greater ? "potion/greater" : "potion/health", color: greater ? "#7dc7ff" : "#4aa3ff" };
  }
  const greater = kind === "hp_greater";
  return { kind: "potion", potType: "hp", name: greater ? "Greater Potion" : "Health Potion",
    heal: greater ? 60 : 35, rarity: greater ? RARITY[2] : RARITY[1],
    model: greater ? "potion/greater" : "potion/health", color: greater ? "#c77dff" : "#e0503a" };
}

export function makePotion(floor = 1) {
  // ~1 in 3 potions are mana potions
  if (Math.random() < 0.32) {
    const greater = Math.random() < 0.2 + floor * 0.01;
    return {
      kind: "potion", potType: "mana",
      name: greater ? "Greater Mana Potion" : "Mana Potion",
      mana: greater ? 70 : 40,
      rarity: greater ? RARITY[2] : RARITY[1],
      model: "potion/mana", color: "#4aa3ff",
    };
  }
  const greater = Math.random() < 0.22 + floor * 0.015;
  return {
    kind: "potion", potType: "hp",
    name: greater ? "Greater Potion" : "Health Potion",
    heal: greater ? 60 : 35,
    rarity: greater ? RARITY[2] : RARITY[1],
    model: greater ? "potion/greater" : "potion/health",
    color: greater ? "#c77dff" : "#e0503a",
  };
}

export function makeCoins(floor = 1) {
  const amount = rndi(3, 10) + Math.floor(floor * 1.5);
  return { kind: "coins", name: `${amount} Gold`, amount, model: "dungeon/coins", rarity: RARITY[0], color: "#f0c34b" };
}

// Big fixed-denomination gold hoards for LOOT CHESTS. Weighted so small piles are
// common and the 10,000 "Dragon's Hoard" is the single rarest drop in the game.
// Higher-value tiers get a mild floor boost so deep runs pay out a little bigger.
const HOARD_TIERS = [
  { amt: 50,    w: 34 },
  { amt: 100,   w: 30 },
  { amt: 250,   w: 20 },
  { amt: 500,   w: 12 },
  { amt: 1000,  w: 6 },
  { amt: 2500,  w: 2.2 },
  { amt: 5000,  w: 0.7 },
  { amt: 10000, w: 0.08 },   // the rarest thing in Relikara
];
export function makeCoinHoard(floor = 1) {
  const boost = 1 + Math.min(1.6, floor * 0.05);   // deeper → big tiers a touch likelier
  const w = HOARD_TIERS.map((t) => (t.amt >= 1000 ? t.w * boost : t.w));
  let total = 0; for (const x of w) total += x;
  let n = Math.random() * total, amt = HOARD_TIERS[0].amt;
  for (let i = 0; i < HOARD_TIERS.length; i++) { if (n < w[i]) { amt = HOARD_TIERS[i].amt; break; } n -= w[i]; }
  const ri = amt >= 10000 ? RARITY.length - 1 : amt >= 5000 ? 3 : amt >= 1000 ? 2 : amt >= 250 ? 1 : 0;
  return { kind: "coins", name: `${amt} Gold`, amount: amt, model: "dungeon/coins", rarity: RARITY[ri], color: "#f0c34b", hoard: amt >= 1000 };
}

// Vault Key — a currency, not bag clutter. Banks to player.keys on pickup (like
// coins bank to gold) and is spent to open locked chests and vault doors.
export function makeKey(amount = 1) {
  return { kind: "key", name: amount > 1 ? `${amount} Vault Keys` : "Vault Key", amount, model: "relic/key_gold", rarity: RARITY[2], color: "#e6b24a" };
}

export const RESOURCES = [
  { name: "Copper Nugget", model: "resource/copper_nugget", color: "#c87f4a" },
  { name: "Copper Bar", model: "resource/copper_bar", color: "#e0975a" },
];
export function makeResource() {
  const base = pick(RESOURCES);
  return { kind: "resource", name: base.name, model: base.model, rarity: RARITY[0], color: base.color, qty: rndi(1, 3) };
}

// ---- Treasure relics (Binbun Treasure pack). Valuable collectibles: no combat
// use, but they sell for good gold — "gold has little meaning inside Relikara;
// relics do." ----
export const TREASURES = [
  { name: "Topaz",            model: "relic/gem_topaz",   color: "#f0c34b", base: 34 },
  { name: "Emerald",          model: "relic/gem_emerald", color: "#4caf6e", base: 46 },
  { name: "Diamond",          model: "relic/gem_diamond", color: "#bfeaff", base: 62 },
  { name: "Silver Ring",      model: "relic/ring",        color: "#cfd8dc", base: 30 },
  { name: "Jeweled Ring",     model: "relic/ring_gem",    color: "#c77dff", base: 54 },
  { name: "Ancient Coin Hoard", model: "relic/coins_mega", color: "#f0c34b", base: 42 },
];
export function makeTreasure(floor = 1) {
  const t = pick(TREASURES);
  const rar = rollRarity();
  const value = Math.max(6, Math.round(t.base * rar.mult * (1 + floor * 0.18)));
  return { kind: "treasure", name: t.name, model: t.model, color: t.color, rarity: rar, value,
    flavor: "A relic of the Vault. Sell it at the Shop for gold." };
}

export function rollDamage(weapon) { return rndi(weapon.dmgMin, weapon.dmgMax); }

// ---- Memory Relics: Legendary weapons earned ONLY by completing Helix sections.
// Each uses a KayKit Fantasy Weapons Bits EXTRA model and carries a SPECIAL
// effect (a strong element plus a passive buff: lifesteal / crit / speed / aoe).
const LEGENDARY = RARITY[RARITY.length - 1];
const LEGENDARY_KINDS = [
  { models: ["legend/sword_d", "legend/sword_e", "legend/sword_f", "legend/sword_g"], wtype: "melee", range: 3.0, cd: 0.5, dmg: [16, 24], element: "fire",   special: { label: "Soulfire — heals you for 12% of damage dealt", lifesteal: 0.12 } },
  { models: ["legend/sword_a", "legend/sword_b", "legend/sword_c"],                   wtype: "melee", range: 2.7, cd: 0.4, dmg: [11, 17], element: "shock",  special: { label: "Stormedge — +18% critical strike", critBonus: 0.18 } },
  { models: ["legend/axe_a", "legend/axe_b", "legend/axe_c", "legend/axe_d"],         wtype: "melee", range: 2.8, cd: 0.6, dmg: [20, 30], element: "fire",   special: { label: "Reaver — heals you for 10% of damage dealt", lifesteal: 0.10 } },
  { models: ["legend/hammer_a", "legend/hammer_b", "legend/hammer_c", "legend/hammer_d"], wtype: "melee", range: 2.8, cd: 0.7, dmg: [24, 36], element: "shock", special: { label: "Thunderfall — shattering shock on every blow", critBonus: 0.1 } },
  { models: ["legend/scythe"],                                                         wtype: "melee", range: 3.0, cd: 0.55, dmg: [18, 27], element: "poison", special: { label: "Soulreaper — heals you for 16% of damage dealt", lifesteal: 0.16 } },
  { models: ["legend/halberd", "legend/spear_a", "legend/spear_b"],                    wtype: "melee", range: 3.4, cd: 0.5, dmg: [17, 25], element: "frost",  special: { label: "Frostreach — long reach and +10% move speed", moveBonus: 0.1 } },
  { models: ["legend/dagger_a", "legend/dagger_b", "legend/dagger_c"],                 wtype: "melee", range: 2.2, cd: 0.2, dmg: [9, 14],  element: "shock",  special: { label: "Twin Fang — +22% crit and +12% move speed", critBonus: 0.22, moveBonus: 0.12 } },
  { models: ["legend/staff_a", "legend/staff_b", "legend/staff_c", "legend/staff_d"], wtype: "ranged", range: 15, cd: 0.6, dmg: [16, 24], element: "arcane", magic: true, aoe: 3.2, special: { label: "Archmage — devastating arcane blast (AoE)", } },
  { models: ["legend/wand_a", "legend/wand_b"],                                        wtype: "ranged", range: 14, cd: 0.45, dmg: [13, 20], element: "fire",  magic: true, aoe: 2.6, special: { label: "Embercaller — searing fire nova (AoE)", } },
  { models: ["legend/bow_a", "legend/bow_b", "legend/bow_c"],                          wtype: "ranged", range: 17, cd: 0.5, dmg: [15, 22], element: "frost",  special: { label: "Winterpierce — freezing shots, +10% crit", critBonus: 0.1 } },
];
export function makeMemoryRelic(section = 1) {
  const A = LEGENDARY_KINDS[(section - 1) % LEGENDARY_KINDS.length];
  const model = A.models[Math.floor((section - 1) / LEGENDARY_KINDS.length) % A.models.length];
  const scale = 1.6 + section * 0.05;                         // strong, scaling with Helix depth
  const element = A.element;
  const e = ELEMENTS[element];
  const sp = A.special || {};
  const w = {
    kind: "weapon", name: relicName(section), wtype: A.wtype,
    range: A.range, cd: A.cd, color: e ? e.color : "#f0a63c",
    rarity: LEGENDARY, model,
    dmgMin: Math.round(A.dmg[0] * LEGENDARY.mult * scale),
    dmgMax: Math.round(A.dmg[1] * LEGENDARY.mult * scale),
    element, elementPower: LEGENDARY.mult * (1 + section * 0.05),
    magic: !!A.magic, aoe: A.aoe || 0,
    named: true, memoryRelic: true, section,
    lifesteal: sp.lifesteal || 0, critBonus: sp.critBonus || 0, moveBonus: sp.moveBonus || 0,
    special: sp.label || "",
    flavor: `A Memory Relic forged at the Helix — Codex ${section} of the Restoration.`,
  };
  ensureMemory(w);
  return w;
}

/** Re-link a deserialized item's `rarity` to the shared RARITY object (JSON
 *  round-trips lose object identity, which RARITY.indexOf(...) relies on). */
export function rehydrateItem(item) {
  if (item && item.rarity && item.rarity.name) {
    const r = RARITY.find((x) => x.name === item.rarity.name);
    if (r) item.rarity = r;
  }
  // legacy: resources used `amount` for quantity before stacking — fold into qty
  if (item && item.kind === "resource" && item.qty == null && item.amount != null) {
    item.qty = item.amount; delete item.amount;
  }
  return item;
}

// ============================================================
//  Hub economy — Shop / Blacksmith / Home
// ============================================================

/** Gold value / price of an item, scaled by rarity + depth. */
export function priceFor(item, floor = 1) {
  if (item.kind === "treasure") return Math.max(5, item.value || 20);   // treasures carry their own value
  const base = { weapon: 34, armor: 30, potion: 18, food: 14, resource: 10 }[item.kind] || 20;
  const rar = item.rarity ? item.rarity.mult : 1;
  let p = base * rar * rar * (1 + floor * 0.22);
  if (item.named) p *= 2.4;                         // named relics are worth far more
  return Math.max(5, Math.round(p));
}

/** What the shop pays to buy an item FROM the player. Treasures barely mark down. */
export function sellPrice(item, floor = 1) {
  const ratio = item.kind === "treasure" ? 0.9 : 0.5;
  return Math.max(2, Math.round(priceFor(item, floor) * ratio));
}
const SELLABLE = { weapon: 1, armor: 1, potion: 1, food: 1, resource: 1, treasure: 1 };
export const isSellable = (item) => !!(item && SELLABLE[item.kind]);

function bumpRarity(it, minTiers = 1) {
  let i = RARITY.indexOf(it.rarity);
  if (i < 0) i = 0;
  // never bump into Legendary — that tier is exclusive to Helix Memory Relics
  const maxIdx = RARITY.length - 2;
  i = Math.min(maxIdx, i + minTiers + (Math.random() < 0.4 ? 1 : 0));
  it.rarity = RARITY[i];
  // rescale stats to the new rarity so the number matches the tier
  const m = it.rarity.mult;
  if (it.kind === "weapon") { it.dmgMin = Math.round(it.dmgMin * (0.9 + m * 0.15)); it.dmgMax = Math.round(it.dmgMax * (0.9 + m * 0.15)); }
  return it;
}

/** Shop stock: powered, mostly-enchanted, higher-rarity relics for sale. */
export function makeShopStock(floor = 1, n = 6) {
  const stock = [];
  for (let i = 0; i < n; i++) {
    const r = Math.random();
    let it;
    if (r < 0.55) it = bumpRarity(makeWeapon(floor + 2), 1);
    else if (r < 0.82) it = bumpRarity(makeArmor(floor + 2), 1);
    else it = makePotion(floor + 1);
    // shop weapons come enchanted
    if (it.kind === "weapon" && !it.element && Math.random() < 0.75) enchantWeapon(it, pick(ELEMENT_KEYS));
    stock.push({ item: it, price: priceFor(it, floor) });
  }
  return stock;
}

/** Blacksmith: forge a weapon sharper (more damage + a rarity tier), but only
 *  up to the rarity your Smithing research allows. `forges` counts uses so the
 *  hub can cap them per research tier. */
export function forgeWeapon(w, rarityCapIdx = RARITY.length - 1) {
  w.dmgMin = Math.round(w.dmgMin * 1.18) + 1;
  w.dmgMax = Math.round(w.dmgMax * 1.18) + 2;
  const i = RARITY.indexOf(w.rarity);
  if (i >= 0 && i < Math.min(RARITY.length - 1, rarityCapIdx)) w.rarity = RARITY[i + 1];
  w.forges = (w.forges || 0) + 1;
  return w;
}

/** Blacksmith: apply / intensify an elemental enchantment on a weapon. `powerCap`
 *  is set by Arcana research. */
export function enchantWeapon(w, element, powerCap = 2.6) {
  const e = ELEMENTS[element]; if (!e) return w;
  const same = w.element === element;
  w.element = element;
  w.elementPower = Math.min(powerCap, (w.elementPower || 0) + (same ? 0.5 : 0.9));
  w.color = e.color;
  const bare = w.name.replace(/^(Flaming|Frost|Venomous|Shocking|Arcane)\s+/, "");
  w.name = `${e.name} ${bare}`;
  return w;
}

/** Blacksmith: reinforce armor. */
export function reinforceArmor(a) {
  a.def += 2; a.hp += 8;
  const i = RARITY.indexOf(a.rarity);
  if (i >= 0 && i < RARITY.length - 1 && Math.random() < 0.5) a.rarity = RARITY[i + 1];
  return a;
}

export const ELEMENT_LIST = ELEMENT_KEYS.slice();

// Starter gear is always plain Common so upgrades feel meaningful.
export function starterWeapon() {
  const w = makeWeapon(1);
  w.rarity = RARITY[0];
  w.name = "Rusty Sword"; w.wtype = "melee"; w.range = 2.6; w.cd = 0.42;
  w.color = "#b9b9b9"; w.dmgMin = 6; w.dmgMax = 10; w.model = "weapon/sword";
  return w;
}
export function starterArmor() {
  return { kind: "armor", name: "Cloth Tunic", color: "#8d6e63",
    rarity: RARITY[0], def: 2, hp: 10 };
}
